import { useState } from "react";
import { motion } from "framer-motion";
import { InstagramCarousel } from "@/components/InstagramCarousel";
import { buyerSlides, sellerSlides } from "@/lib/carouselData";

type Tab = "buyer" | "seller";

export default function Index() {
  const [activeTab, setActiveTab] = useState<Tab>("buyer");

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{ background: "oklch(0.12 0.04 240)" }}
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 30 }}
        className="text-center mb-8 max-w-lg mx-auto"
      >
        <div className="flex items-center justify-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-lg"
            style={{
              background: "oklch(0.68 0.12 45)",
              color: "oklch(0.15 0.02 240)",
            }}
          >
            SK
          </div>
          <span
            className="font-semibold tracking-widest text-xs uppercase"
            style={{ color: "oklch(0.68 0.12 45)" }}
          >
            Saskatoon Real Estate
          </span>
        </div>
        <h1
          className="text-white font-bold mb-2"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(1.8rem, 6vw, 2.6rem)",
          }}
        >
          Instagram Carousel Posts
        </h1>
        <p className="text-white/50 text-sm">
          Click arrows or dots to navigate each slide
        </p>
      </motion.div>

      {/* Tab switcher */}
      <div className="flex justify-center mb-8">
        <div
          className="flex rounded-full p-1 gap-1"
          style={{ background: "oklch(0.20 0.05 240)" }}
        >
          {(["buyer", "seller"] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="relative px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer"
              style={{
                color:
                  activeTab === tab
                    ? "oklch(0.15 0.02 240)"
                    : "oklch(0.65 0.02 240)",
              }}
            >
              {activeTab === tab && (
                <motion.div
                  layoutId="tab-pill"
                  className="absolute inset-0 rounded-full"
                  style={{ background: "oklch(0.68 0.12 45)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 35 }}
                />
              )}
              <span className="relative z-10">
                {tab === "buyer" ? "🏡 Buyer Journey" : "💰 Seller Journey"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Carousel */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 30 }}
        className="flex justify-center"
      >
        <InstagramCarousel
          slides={activeTab === "buyer" ? buyerSlides : sellerSlides}
          accentColor="oklch(0.68 0.12 45)"
        />
      </motion.div>

      {/* Slide count info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center mt-6"
      >
        <p className="text-white/30 text-xs tracking-wide">
          {activeTab === "buyer"
            ? `${buyerSlides.length} slides • Buyer's complete journey`
            : `${sellerSlides.length} slides • Seller's complete journey`}
        </p>
      </motion.div>

      {/* Caption section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 240, damping: 30 }}
        className="max-w-lg mx-auto mt-10 rounded-2xl p-6"
        style={{ background: "oklch(0.18 0.05 240)" }}
      >
        <h2
          className="font-bold mb-3 text-lg"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "oklch(0.68 0.12 45)",
          }}
        >
          📱 Instagram Caption
        </h2>
        {activeTab === "buyer" ? (
          <div className="text-white/75 text-xs leading-relaxed space-y-2">
            <p>
              🏡✨ <strong className="text-white">YOUR COMPLETE HOME BUYING ROADMAP</strong> — Step by Step!
            </p>
            <p>
              Thinking about buying in Saskatoon? Swipe through to see exactly what the process looks like from your first meeting with me to 🔑 Possession Day!
            </p>
            <p className="text-white/50">
              Drop a 🏡 in the comments or DM me to start your journey today!
            </p>
            <p className="text-white/40 text-[10px] mt-2">
              #HomeBuyer #SaskatchewanRealEstate #SaskatoonHomes #BuyingAHome #FirstTimeHomeBuyer #RealEstateTips #PossessionDay #YXE #YXEHomes #MortgagePreApproval #HomeOwnership
            </p>
          </div>
        ) : (
          <div className="text-white/75 text-xs leading-relaxed space-y-2">
            <p>
              💰🏡 <strong className="text-white">YOUR COMPLETE HOME SELLING ROADMAP</strong> — From Listing to SOLD!
            </p>
            <p>
              Thinking about selling your Saskatoon home? Swipe through to see everything that happens from the moment you say "let's do it" to handing over the keys!
            </p>
            <p className="text-white/50">
              Drop a 💰 in the comments or DM me to create your selling strategy today!
            </p>
            <p className="text-white/40 text-[10px] mt-2">
              #HomeSeller #JustSold #SaskatchewanRealEstate #SaskatoonHomes #ListWithMe #RealEstateTips #FINTRAC #PossessionDay #YXE #YXESold #ConveyancingCanada
            </p>
          </div>
        )}
      </motion.div>

      {/* Footer */}
      <p
        className="text-center text-xs mt-8 tracking-widest uppercase"
        style={{ color: "oklch(0.40 0.03 240)" }}
      >
        Saskatoon Real Estate • Saskatchewan, Canada
      </p>
    </div>
  );
}
